import { Button, Checkbox, Row, TextArea, TextField, H5 } from '@fs/zion-ui'
import React, { useState } from 'react'
import { usePageView } from '@fs/zion-analytics'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { createEntityRef, createMultipleEntityRefs } from '../../../services/ordinanceService'
import { getOwsSummary } from '../../../services/owsService'
import { getPersonName } from '../../../services/personService'
import { isAlphaNumericPersonId, isOwsId } from '../../../util/EntityUtil'
import { getLastPathElement } from '../../../util/StringUtil'

export default function CreateOSR() {
  usePageView({ page_detail: 'DMConsole:CreateOSR' })
  const { error, ok } = useNotifications()
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tools.createOsr'))
  const [byOwsId, setByOwsId] = useState(false)
  const [principalPersonId, setPrincipalPersonId] = useState()
  const [fatherPersonId, setFatherPersonId] = useState()
  const [motherPersonId, setMotherPersonId] = useState()
  const [wifePersonId, setWifePersonId] = useState()
  const [owsId, setOwsId] = useState()
  const [reason, setReason] = useState()
  const [ctrIds, setCtrIds] = useState()
  const [fetching, setFetching] = useState(false)
  const [ows, setOws] = useState()
  const [personNames, setPersonNames] = useState({})

  const resetFields = (resetAll) => {
    if (resetAll) {
      setOwsId()
      setCtrIds()
    }
    setPrincipalPersonId()
    setFatherPersonId()
    setMotherPersonId()
    setWifePersonId()
    setReason()
    setOws()
    setPersonNames({})
  }

  const byOwsIdCheckboxOnChange = ({ target }) => {
    resetFields(true)
    setByOwsId(target.checked)
  }

  const createOsrOnClick = async () => {
    if (byOwsId) {
      try {
        const createEntityRefRequest = {
          ctrId: undefined,
          owsId,
          principalPersonId: personNames.principal && principalPersonId,
          fatherPersonId: personNames.father && fatherPersonId,
          motherPersonId: personNames.mother && motherPersonId,
          wifePersonId: personNames.wife && wifePersonId,
          reason,
        }
        await createEntityRef(createEntityRefRequest)
        ok(t('success', 'Success'))
        resetFields(true)
      } catch (err) {
        const rolesToCreateOsrFor = [principalPersonId, fatherPersonId, motherPersonId, wifePersonId].filter(
          (role) => Boolean(role) !== false
        )
        error(
          rolesToCreateOsrFor.length > 1
            ? t('tools.errors.creatingEachOsr', {
                defaultValue: `Error creating each OSR: ${err.message || ''}`,
                message: err.message || '',
              })
            : t('tools.errors.creatingOsr', {
                defaultValue: `Error creating OSR: ${err.message || ''}`,
                message: err.message || '',
              }),
          err
        )
      }
    } else {
      const ctrIdsList = ctrIds
        .trim()
        .split(/,\n|;\n|\n|,|;|\s+/g)
        .filter((id) => id !== '')
      const createEntityRefRequests = ctrIdsList.map((ctrId) => {
        return {
          ctrId,
          owsId,
          principalPersonId,
          fatherPersonId,
          motherPersonId,
          wifePersonId,
          reason,
        }
      })
      await createMultipleEntityRefs(createEntityRefRequests)
        .then((response) => {
          if (response.data?.notifications?.length > 0) {
            error(
              t('tools.errors.creatingOsrWith', {
                defaultValue: `Error creating OSR with the following CTR IDs: ${response.data.notifications?.[0]?.msg}`,
                ids: response.data.notifications?.[0]?.msg,
              })
            )
          } else {
            ok(t('success', 'Success'))
            resetFields(true)
          }
        })
        .catch((err) => {
          error(`${t('tools.errors.creatingOsr', { defaultValue: `Error creating OSR: `, message: err })}}`)
        })
    }
  }

  const onOwsIdChange = async (input) => {
    const inputWithoutWhitespace = input.trim()
    setOwsId(inputWithoutWhitespace)
    resetFields(false)
    if (isOwsId(inputWithoutWhitespace)) {
      try {
        setFetching(true)
        const owsSummary = await getOwsSummary(inputWithoutWhitespace)
        setFetching(false)
        if (owsSummary) {
          setOws(owsSummary)
        }
      } catch (err) {
        error(
          t('tools.errors.retrievingOws', {
            defaultValue: `Error retrieving ows: ${err.message || ''}`,
            message: err.message || '',
          }),
          err
        )
        setFetching(false)
      }
    }
  }

  const onPersonIdChange = async (input, person) => {
    const inputWithoutWhitespace = input.trim()
    switch (person) {
      case 'principal': {
        setPrincipalPersonId(inputWithoutWhitespace)
        break
      }
      case 'father': {
        setFatherPersonId(inputWithoutWhitespace)
        break
      }
      case 'mother': {
        setMotherPersonId(inputWithoutWhitespace)
        break
      }
      case 'wife': {
        setWifePersonId(inputWithoutWhitespace)
        break
      }
      default: {
        break
      }
    }
    setPersonNames((prevValue) => {
      delete prevValue[person]
      return prevValue
    })
    if (isAlphaNumericPersonId(inputWithoutWhitespace)) {
      try {
        setFetching(true)
        const personName = await getPersonName(inputWithoutWhitespace)
        setFetching(false)
        if (personName !== 'Not Found') {
          setPersonNames((prevValue) => {
            return { ...prevValue, [person]: personName }
          })
        }
      } catch (err) {
        error(
          t('tools.errors.retrievingPersonName', {
            defaultValue: `Error getting person name: ${err.message || ''}`,
            message: err.message || '',
          }),
          err
        )
        setFetching(false)
      }
    }
  }

  const getValidOwsMessage = (input, potentialOws) => {
    if (isOwsId(input)) {
      if (potentialOws) {
        return t('tools.createOSR.byOwsId.input.owsId.messages.owsFound', 'OWS Found')
      }
      return t('tools.createOSR.byOwsId.input.owsId.messages.validOwsId', 'Valid OWS ID')
    }
    return null
  }

  const getOwsErrorMessage = (input, potentialOws) => {
    if (fetching) {
      return null
    }
    if (!owsId) {
      return null
    }
    if (!isOwsId(input)) {
      return t('tools.createOSR.byOwsId.input.owsId.messages.invalidOwsId', 'Invalid OWS ID')
    }
    if (!potentialOws) {
      return t('tools.createOSR.byOwsId.input.owsId.messages.owsNotFound', 'OWS Not Found')
    }
    return null
  }

  const getValidPersonIdMessage = (input, potentialPersonName) => {
    if (isAlphaNumericPersonId(input)) {
      if (potentialPersonName !== 'Not Found') {
        return t('tools.createOSR.byOwsId.input.person.messages.personName', '{potentialPersonName}', {
          potentialPersonName,
        })
      }
      return t('tools.createOSR.byOwsId.input.person.messages.validPersonId', 'Valid Person ID')
    }
    return null
  }

  const getPersonErrorMessage = (input, potentialPersonName, person) => {
    if (fetching) {
      return null
    }
    switch (person) {
      case 'principal': {
        if (!principalPersonId) return null
        break
      }
      case 'father': {
        if (!fatherPersonId) return null
        break
      }
      case 'mother': {
        if (!motherPersonId) return null
        break
      }
      case 'wife': {
        if (!wifePersonId) return null
        break
      }
      default: {
        break
      }
    }
    if (!isAlphaNumericPersonId(input)) {
      return t('tools.createOSR.byOwsId.input.person.messages.invalidPersonId', 'Invalid Person ID')
    }
    if (!potentialPersonName) {
      return t('tools.createOSR.byOwsId.input.person.messages.notFound', 'Not Found')
    }
    return null
  }

  const dmcOwsCtrList = ows?.data?.templePlatform?.ctrs
  const personOptions = dmcOwsCtrList ? (
    <Row>
      <TextField
        label={t('principal', 'Principal')}
        value={principalPersonId}
        onChange={(e) => onPersonIdChange(e.target.value, 'principal')}
        type="text"
        validMessage={getValidPersonIdMessage(principalPersonId, personNames.principal)}
        errorMessage={getPersonErrorMessage(principalPersonId, personNames.principal, 'principal')}
      />
      {dmcOwsCtrList.some(
        (qualifiedOrdinance) => getLastPathElement(qualifiedOrdinance.ordinanceType) === 'SEALING_TO_PARENTS'
      ) && (
        <Row>
          <TextField
            label={t('father', 'Father')}
            value={fatherPersonId}
            onChange={(e) => onPersonIdChange(e.target.value, 'father')}
            type="text"
            disabled={!ows}
            validMessage={getValidPersonIdMessage(fatherPersonId, personNames.father)}
            errorMessage={getPersonErrorMessage(fatherPersonId, personNames.father, 'father')}
          />
          <TextField
            label={t('mother', 'Mother')}
            value={motherPersonId}
            onChange={(e) => onPersonIdChange(e.target.value, 'mother')}
            type="text"
            disabled={!ows}
            validMessage={getValidPersonIdMessage(motherPersonId, personNames.mother)}
            errorMessage={getPersonErrorMessage(motherPersonId, personNames.mother, 'mother')}
          />
        </Row>
      )}
      {dmcOwsCtrList.some(
        (qualifiedOrdinance) => getLastPathElement(qualifiedOrdinance.ordinanceType) === 'SEALING_TO_SPOUSE'
      ) && (
        <TextField
          label={t('wife', 'Wife')}
          value={wifePersonId}
          onChange={(e) => onPersonIdChange(e.target.value, 'wife')}
          type="text"
          disabled={!ows}
          validMessage={getValidPersonIdMessage(wifePersonId, personNames.wife)}
          errorMessage={getPersonErrorMessage(wifePersonId, personNames.wife, 'wife')}
        />
      )}
    </Row>
  ) : null

  const byOwsIdInputs = (
    <>
      <Row>
        <TextField
          label={t('ows.owsId', 'OWS ID')}
          value={owsId}
          onChange={(e) => onOwsIdChange(e.target.value)}
          type="text"
          validMessage={getValidOwsMessage(owsId, ows)}
          errorMessage={getOwsErrorMessage(owsId, ows)}
        />
      </Row>
      {ows && (
        <>
          {personOptions}
          <Row>
            <TextField
              label={t('reason', 'Reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              type="text"
              disabled={!principalPersonId && !wifePersonId && !fatherPersonId && !motherPersonId}
              growX
            />
          </Row>
          <Row>
            <Button
              onClick={createOsrOnClick}
              emphasis="high"
              disabled={!personNames.principal && !personNames.wife && !personNames.father && !personNames.mother}
            >
              {t('action.create', 'Create')}
            </Button>
          </Row>
        </>
      )}
    </>
  )
  const byCtrIdInputs = (
    <Row>
      <TextArea
        id="ctrIdsTextArea"
        label={t('tools.createOSR.byCtrId.input.ctrIds', "CTR ID's")}
        value={ctrIds}
        onChange={(e) => setCtrIds(e.target.value)}
        type="text"
        disableResize
      />
      <Button onClick={createOsrOnClick} emphasis="high" disabled={!ctrIds} alignY="nextToInput">
        {t('action.create', 'Create')}
      </Button>
    </Row>
  )

  return (
    <>
      <Row>
        <H5 growX supplementary>
          {byOwsId
            ? t(
                'tools.createOSR.byOwsId.description',
                'Creates an Ordinance Source Reference using a Person ID and an OWS ID.'
              )
            : t('tools.createOSR.byCtrId.description', 'Creates an Ordinance Source Reference using a CTR ID.')}
        </H5>
        <Checkbox
          name="creationType"
          label={t('tools.createOSR.byOwsId.byOwsId', 'By OWS ID')}
          checked={byOwsId}
          onChange={byOwsIdCheckboxOnChange}
        />
      </Row>
      {byOwsId ? byOwsIdInputs : byCtrIdInputs}
    </>
  )
}
